const formatDate = (date) =>
    date
        ? new Date(date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        })
        : "—";

const formatTime = (date) =>
    date
        ? new Date(date).toLocaleTimeString("en-IN", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        })
        : "";

const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "—";

    const numericValue = Number(String(value).replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(numericValue)) return `₹${numericValue.toLocaleString("en-IN")}`;

    return String(value);
};

const websiteUrl = (website) =>
    /^https?:\/\//i.test(website.trim()) ? website.trim() : `https://${website.trim()}`;

const websiteLabel = (website) => {
    try {
        return new URL(websiteUrl(website)).hostname.replace(/^www\./, "");
    } catch {
        return website;
    }
};

function DateCell({ value }) {
    return (
        <div className="min-w-0">
            <p className="crm-cell-main">{formatDate(value)}</p>
            {value && <p className="crm-cell-sub">{formatTime(value)}</p>}
        </div>
    );
}

export const DEFAULT_COLUMNS = [
    {
        id: "company",
        label: "Company",
        width: 270,
        visible: true,
        renderCell: (lead) => (
            <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#294657]" title={lead.company}>
                    {lead.company || "No company"}
                </p>
                <p className="mt-0.5 truncate text-[13px] text-[#6d8493]">
                    {[lead.designation, lead.company_location].filter(Boolean).join(" · ") || "No company details"}
                </p>
                {lead.website && (
                    <a
                        href={websiteUrl(lead.website)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        title={lead.website}
                        className="mt-1 inline-block max-w-full truncate text-xs font-semibold text-[#2f7faf] hover:text-[#176b9a] hover:underline"
                    >
                        {websiteLabel(lead.website)}
                    </a>
                )}
            </div>
        ),
    },
    {
        id: "lead_source",
        label: "Lead source",
        width: 230,
        visible: true,
        renderCell: (lead) => (
            <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                    <span className="max-w-[140px] truncate rounded-lg border border-[#d5e9f1] bg-[#eff8fb] px-2 py-1 text-xs font-bold text-[#2b759f]">
                        {lead.source || "Unknown"}
                    </span>
                    {lead.priority_area_type && (
                        <span className="truncate text-xs font-semibold text-[#8197a4]">
                            {lead.priority_area_type}
                        </span>
                    )}
                </div>
                <p className="mt-1.5 truncate text-[13px] font-medium text-[#405c6d]">
                    {lead.priority_area || "No priority area"}
                </p>
                <p className="mt-1 text-xs text-[#8197a4]">
                    Team {lead.team_size || "—"} <span className="px-1 text-[#bfd0d8]">•</span> {formatMoney(lead.revenue)}
                </p>
            </div>
        ),
    },
    {
        id: "current_stage",
        label: "Current stage",
        width: 230,
        visible: true,
        renderCell: (lead) => (
            lead.stage ? (
                <div className="min-w-0">
                    <span className={`crm-stage-pill${lead.stage.is_closed ? (Number(lead.stage.probability) >= 100 ? " is-won" : " is-lost") : ""}`} title={lead.stage.name}>
                        <i aria-hidden="true" />{lead.stage.name}
                    </span>
                    <p className="crm-cell-sub" title={lead.current_state?.current_node?.name || ""}>
                        {lead.current_state?.current_node?.name || "No active step"}
                        {lead.stage.probability !== null && lead.stage.probability !== undefined && <> · {lead.stage.probability}%</>}
                    </p>
                </div>
            ) : (
                <span className="crm-stage-pill is-none">Not assigned</span>
            )
        ),
    },
    {
        id: "assigned_to",
        label: "Owner",
        width: 210,
        visible: true,
        renderCell: (lead) => {
            const assignedInitials = lead.assigned_user?.name
                ? lead.assigned_user.name
                    .split(/\s+/)
                    .map((name) => name[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)
                : "";

            return lead.assigned_user ? (
                <div className="crm-owner-cell">
                    <span className="crm-owner-mono" aria-hidden="true">{assignedInitials}</span>
                    <span className="min-w-0">
                        <span className="crm-cell-main" title={lead.assigned_user.email}>{lead.assigned_user.name}</span>
                    </span>
                </div>
            ) : (
                <span className="crm-stage-pill is-none">Unassigned</span>
            );
        },
    },
    {
        id: "stage_updated",
        label: "Stage updated",
        width: 180,
        visible: true,
        sortField: "stage_updated_at",
        renderCell: (lead) => <DateCell value={lead.stage_changed_at} />,
    },
    {
        id: "created",
        label: "Created",
        width: 160,
        visible: true,
        sortField: "created_at",
        renderCell: (lead) => <DateCell value={lead.created_at} />,
    },
    {
        id: "updated",
        label: "Updated",
        width: 160,
        visible: true,
        sortField: "updated_at",
        renderCell: (lead) => <DateCell value={lead.updated_at} />,
    },
];
